/* =========================================================================
   b2g2-cart.js  —  Buy 2 Get 2 bandana engine (no app)
   -------------------------------------------------------------------------
   Responsibilities:
     - Count "paid units" from the cart using pack_count (line properties + config)
     - Drive the 3-node tracker
     - Show the correct message per scenario:
         * 4-pack added        -> "You got 2 bandanas free 🎉" (50% via native discount)
         * 2-pack OR 2 singles -> unlock gift picker, choose 2 free singles
     - Render the gift picker with ALL singles (fix #4)
     - Add/remove $0 gift lines (native BXGY zeroes them at checkout)
     - Keep exactly `freeGiftCount` free gifts, no more, no less
   Depends on window.B2G2_CONFIG (from b2g2-config.liquid).
   ========================================================================= */
(function () {
  "use strict";

  var CFG = window.B2G2_CONFIG;
  if (!CFG) { console.warn("[b2g2] missing B2G2_CONFIG"); return; }

  var giftProp = CFG.giftProperty;   // "_b2g2_free_gift"
  var THRESHOLD = CFG.unlockThreshold; // 2
  var FREE_N = CFG.freeGiftCount;      // 2

  /* ---------- tiny helpers ---------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  // Format cents using the shop money format (mirrors b2g2-drawer.js).
  function money(cents) {
    var fmt = (CFG.moneyFormat || "${{amount}}");
    var amount = (cents / 100).toFixed(2);
    if (fmt.indexOf("{{amount}}") !== -1) return fmt.replace("{{amount}}", amount);
    if (fmt.indexOf("{{amount_no_decimals}}") !== -1) return fmt.replace("{{amount_no_decimals}}", Math.round(cents / 100));
    return "$" + amount;
  }

  function getCart() {
    return fetch("/cart.js", { headers: { "Accept": "application/json" } })
      .then(function (r) { return r.json(); });
  }

  function changeCart(body) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function addToCart(items) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ items: items })
    }).then(function (r) { return r.json(); });
  }

  /* ---------- lookups ---------- */
  // Set of all single-bandana variant ids (from giftProducts sizes).
  // Backward-compatible: still support old giftPool if present.
  var SINGLE_VARIANT_IDS = {};
  (CFG.giftProducts || []).forEach(function (p) {
    (p.sizes || []).forEach(function (s) { SINGLE_VARIANT_IDS[s.id] = true; });
  });
  (CFG.giftPool || []).forEach(function (g) { SINGLE_VARIANT_IDS[g.id] = true; });

  // Full variantId -> packCount for ALL bandanas (single/2-pack/4-pack).
  var PACK_LOOKUP = CFG.packLookup || {};

  // A cart line is a "single bandana" if its variant is in the single set.
  function isSingle(line) { return !!SINGLE_VARIANT_IDS[line.variant_id]; }

  // pack count for a cart line. Priority:
  //   1. explicit line property (_b2g2_pack) if a form injected it
  //   2. full packLookup by variant id (covers 2-pack & 4-pack reliably)
  //   3. single-variant set (singles = 1)
  //   4. 0 = not part of the offer
  function packCountOf(line) {
    var propPc = lineProp(line, "_b2g2_pack");
    if (propPc != null) return parseInt(propPc, 10) || 0;
    if (PACK_LOOKUP[line.variant_id] != null) return parseInt(PACK_LOOKUP[line.variant_id], 10) || 0;
    if (SINGLE_VARIANT_IDS[line.variant_id]) return 1;
    return 0;
  }

  function lineProp(line, key) {
    return line.properties && line.properties[key] != null ? line.properties[key] : null;
  }

  function isGiftLine(line) {
    return lineProp(line, giftProp) === "true" || lineProp(line, giftProp) === true;
  }

  /* ---------- the core math ---------- *
     paidUnits = sum of pack_count across NON-gift bandana lines * quantity.
     Examples:
       1 x 4-pack  -> 4 units  (self-contained: 2 paid + 2 free already, no picker)
       1 x 2-pack  -> 2 units  (unlocks picker for 2 free singles)
       2 x single  -> 2 units  (unlocks picker for 2 free singles)
       1 x single  -> 1 unit   (prompt: add 1 more)
  */
  function analyze(cart) {
    var paidUnits = 0;
    var hasFourPack = false;
    var hasTwoPack = false;
    var giftLines = [];

    cart.items.forEach(function (line) {
      var pc = packCountOf(line);
      if (isGiftLine(line)) { giftLines.push(line); return; }
      if (pc <= 0) return; // not a bandana in the offer
      paidUnits += pc * line.quantity;
      if (pc >= 4) hasFourPack = true;
      else if (pc === 2) hasTwoPack = true;
    });

    // 4-pack is self-contained. Its "free 2" is delivered as 50% off the SKU,
    // so it should NOT consume the singles-picker allotment.
    // We compute picker eligibility from units that are NOT from 4-packs.
    var pickerUnits = 0;
    cart.items.forEach(function (line) {
      if (isGiftLine(line)) return;
      var pc = packCountOf(line);
      if (pc <= 0 || pc >= 4) return; // exclude 4-packs
      pickerUnits += pc * line.quantity;
    });

    var pickerUnlocked = pickerUnits >= THRESHOLD;
    var currentGiftQty = giftLines.reduce(function (n, l) { return n + l.quantity; }, 0);

    return {
      paidUnits: paidUnits,
      pickerUnits: pickerUnits,
      hasFourPack: hasFourPack,
      hasTwoPack: hasTwoPack,
      pickerUnlocked: pickerUnlocked,
      giftLines: giftLines,
      currentGiftQty: currentGiftQty,
      // remaining picks available (only when unlocked)
      remainingPicks: pickerUnlocked ? Math.max(0, FREE_N - currentGiftQty) : 0
    };
  }

  /* ---------- tracker rendering ---------- */
  function renderTracker(state) {
    var root = $("[data-b2g2-tracker]");
    if (!root) return;

    // node completion: node1 at >=1 picker unit, node2 at >=2, node3 when unlocked.
    // 4-pack path completes all three immediately (already 2+2).
    var u = state.hasFourPack ? 2 : state.pickerUnits;
    var n1 = u >= 1;
    var n2 = u >= 2;
    var n3 = state.hasFourPack || state.pickerUnlocked;

    setNode(root, 1, n1);
    setNode(root, 2, n2);
    setNode(root, 3, n3);

    var pct = n3 ? 100 : (n2 ? 50 : (n1 ? 0 : 0));
    // progress fill spans between node1 and node3
    var fill = 0;
    if (u >= 2 || n3) fill = 100;
    else if (u >= 1) fill = 50;
    var prog = $("[data-b2g2-progress]", root);
    if (prog) prog.style.width = fill + "%";
  }

  function setNode(root, i, done) {
    var node = $('[data-b2g2-node="' + i + '"]', root);
    if (node) node.classList.toggle("is-complete", !!done);
  }

  /* ---------- message + picker visibility ---------- */
  function renderMessaging(state) {
    var picker = $("[data-b2g2-gift]");
    var promptEl = picker ? $("[data-b2g2-gift-prompt]", picker) : null;

    // Scenario A: a 4-pack is present -> celebratory, no picker (unless a 2-pack/singles
    // path ALSO unlocked a separate pair, which the picker handles independently).
    var banner = ensureBanner();

    if (state.hasFourPack && !state.pickerUnlocked) {
      banner.hidden = false;
      banner.textContent = "🎉 4-Pack Bundle is set! (2 paid + 2 free)";
      if (picker) picker.hidden = true;
     // hideReco();
     renderReco(state);
      return;
    }

    if (state.pickerUnlocked) {
      // 2-pack or 2 singles path
      hideReco();
      banner.hidden = false;
      var picksLeft = state.remainingPicks;
      if (picksLeft > 0) {
        banner.textContent = "🎉 CHOOSE ANY 2 BANDANAS FOR FREE BELOW";
      } else {
        banner.textContent = "🎉 Your 2 free bandanas are in the bag!";
      }
      if (picker) {
        picker.hidden = false;
        if (promptEl) {
          promptEl.textContent = picksLeft > 0
            ? "Choose your " + picksLeft + " free gift" + (picksLeft > 1 ? "s" : "") + " before checkout."
            : "You've selected your free gifts.";
        }
      }
    } else {
      // Not unlocked yet
      banner.hidden = false;
      var need = THRESHOLD - state.pickerUnits;
      if (state.pickerUnits <= 0 && !state.hasFourPack) {
        banner.hidden = true; // empty-ish state, say nothing loud
      } else {
        banner.textContent = "ONE MORE BANDANA = 2 FREE";
      }
      if (picker) picker.hidden = true;
      // Show recommended singles when they have at least one bandana but haven't unlocked
      if (state.pickerUnits >= 1 && !state.hasFourPack) {
        renderReco(state);
      } else {
        hideReco();
      }
    }
  }

  /* ---------- recommended singles (paid) to reach threshold ---------- */


/* ---------- recommended Best Sellers (paid), excluding singles ---------- */
function hideReco() {
  var reco = $("[data-b2g2-reco]");
  if (reco) reco.hidden = true;
}

var recoProductsPromise = null;

function getRecoProducts() {
  if (recoProductsPromise) return recoProductsPromise;

  // Shopify collection handle
  var handle = CFG.recoCollectionHandle || "best-selling";

  recoProductsPromise = fetch(
    "/collections/" + encodeURIComponent(handle) + "/products.json?limit=50",
    { headers: { "Accept": "application/json" } }
  )
    .then(function (r) {
      if (!r.ok) {
        throw new Error("Could not load recommendation collection: " + handle);
      }

      return r.json();
    })
    .then(function (data) {
  var collectionProducts = data.products || [];

  return Promise.all(
    collectionProducts.map(function (p) {

      // Fetch the full Shopify product JSON
      return fetch("/products/" + p.handle + ".js", {
        headers: { "Accept": "application/json" }
      })
        .then(function (r) {
          if (!r.ok) return null;
          return r.json();
        })
        .then(function (fullProduct) {
          if (!fullProduct) return null;

          /*
           * EXCLUDE ANY PRODUCT TAGGED "Single"
           */
          var tags = fullProduct.tags || [];

          if (typeof tags === "string") {
            tags = tags.split(",");
          }

          var isSingle = tags.some(function (tag) {
            return String(tag).trim().toLowerCase() === "single";
          });

          if (isSingle) {
            return null;
          }

          /*
           * Only keep available variants
           */
          var sizes = (fullProduct.variants || [])
            .filter(function (v) {
              return v.available !== false;
            })
            .map(function (v) {
              return {
                id: v.id,
                title: v.title,
                price: parseInt(v.price, 10) || 0
              };
            });

          if (!sizes.length) return null;

          /*
           * Product image
           */
          var image = "";

          if (fullProduct.featured_image) {
            image =
              typeof fullProduct.featured_image === "string"
                ? fullProduct.featured_image
                : fullProduct.featured_image.src || "";
          } else if (
            fullProduct.images &&
            fullProduct.images.length
          ) {
            image =
              typeof fullProduct.images[0] === "string"
                ? fullProduct.images[0]
                : fullProduct.images[0].src || "";
          }

          return {
            id: fullProduct.id,
            title: fullProduct.title,
            image: image,
            sizes: sizes
          };
        })
        .catch(function (e) {
          console.warn(
            "[b2g2] could not inspect product:",
            p.handle,
            e
          );

          return null;
        });
    })
  );
})
.then(function (products) {
  return products.filter(Boolean);
})
    .catch(function (e) {
      console.warn("[b2g2] recommendation collection error", e);
      return [];
    });

  return recoProductsPromise;
}

function renderReco(state) {
  var reco = $("[data-b2g2-reco]");
  var track = $("[data-b2g2-reco-track]");
  var tpl = $("[data-b2g2-reco-template]");

  if (!reco || !track || !tpl) return;

  // Keep hidden while collection loads
  reco.hidden = true;

  getRecoProducts().then(function (products) {
    track.innerHTML = "";

    products.forEach(function (p) {
      var sizes = p.sizes || [];

      if (!sizes.length) return;

      var node = tpl.content.firstElementChild.cloneNode(true);

      var img = $(".b2g2-card__img", node);

      if (img) {
        img.src = p.image || "";
        img.alt = p.title;
      }

      var nameEl = $("[data-name]", node);

      if (nameEl) {
        nameEl.textContent = p.title;
      }

      var priceEl = $("[data-price]", node);
      var select = $("[data-b2g2-size]", node);
      var btn = $("[data-b2g2-add-single]", node);

      if (select) {
        select.innerHTML = "";

        sizes.forEach(function (s) {
          var opt = document.createElement("option");

          opt.value = s.id;
          opt.textContent = s.title;
          opt.setAttribute("data-price", s.price);

          select.appendChild(opt);
        });
      }

      function syncPrice() {
        var opt =
          select && select.options
            ? select.options[select.selectedIndex]
            : null;

        var cents = opt
          ? parseInt(opt.getAttribute("data-price"), 10)
          : sizes[0].price;

        if (priceEl) {
          priceEl.textContent = money(cents);
        }

        if (btn) {
          btn.setAttribute(
            "data-variant-id",
            select ? select.value : sizes[0].id
          );
        }
      }

      if (select) {
        select.addEventListener("change", syncPrice);
        syncPrice();
      } else if (btn) {
        btn.setAttribute("data-variant-id", sizes[0].id);

        if (priceEl) {
          priceEl.textContent = money(sizes[0].price);
        }
      }

      track.appendChild(node);
    });

    // Only show recommendation section if products remain
    reco.hidden = products.length === 0;
  });
}


/*
  function hideReco() {
    var reco = $("[data-b2g2-reco]");
    if (reco) reco.hidden = true;
  }

  function renderReco(state) {
    var reco = $("[data-b2g2-reco]");
    var track = $("[data-b2g2-reco-track]");
    var tpl = $("[data-b2g2-reco-template]");
    if (!reco || !track || !tpl) return;

    // variant ids already in the cart (don't recommend what they already have)
    var inCart = {};
    (state.giftLines || []).forEach(function (l) { inCart[l.variant_id] = true; });

    track.innerHTML = "";
    (CFG.giftProducts || []).forEach(function (p) {
      var sizes = p.sizes || [];
      if (!sizes.length) return;

      var node = tpl.content.firstElementChild.cloneNode(true);
      var img = $(".b2g2-card__img", node);
      img.src = p.image || "";
      img.alt = p.title;
      $("[data-name]", node).textContent = p.title;

      var priceEl = $("[data-price]", node);
      var select = $("[data-b2g2-size]", node);
      var btn = $("[data-b2g2-add-single]", node);

      if (select) {
        select.innerHTML = "";
        sizes.forEach(function (s) {
          var opt = document.createElement("option");
          opt.value = s.id;
          opt.textContent = s.title;
          opt.setAttribute("data-price", s.price);
          select.appendChild(opt);
        });
      }
      function syncPrice() {
        var opt = select.options[select.selectedIndex];
        var cents = opt ? parseInt(opt.getAttribute("data-price"), 10) : sizes[0].price;
        if (priceEl) priceEl.textContent = money(cents);
        btn.setAttribute("data-variant-id", select.value);
      }
      if (select) { select.addEventListener("change", syncPrice); syncPrice(); }

      track.appendChild(node);
    });

    reco.hidden = false;
  }
*/
function addPaidSingle(variantId) {
  return addToCart([{
    id: variantId,
    quantity: 1,
    properties: {
      "_b2g2_pack": "1"
    }
  }]).then(refresh);
}

  function ensureBanner() {
    var b = $("[data-b2g2-banner]");
    if (!b) {
      b = document.createElement("div");
      b.setAttribute("data-b2g2-banner", "");
      b.className = "b2g2-banner";
      var tracker = $("[data-b2g2-tracker]");
      if (tracker && tracker.parentNode) {
        tracker.parentNode.insertBefore(b, tracker.nextSibling);
      } else {
        document.body.appendChild(b);
      }
    }
    return b;
  }

  /* ---------- gift picker rendering: ONE card per product + size dropdown ---------- */
  function renderPicker(state) {
    var track = $("[data-b2g2-gift-track]");
    var tpl = $("[data-b2g2-card-template]");
    if (!track || !tpl) return;

    // which variant ids are already chosen as gifts
    var chosenIds = {};
    state.giftLines.forEach(function (l) { chosenIds[l.variant_id] = true; });

    var products = CFG.giftProducts || [];
    track.innerHTML = "";

    products.forEach(function (p) {
      var sizes = p.sizes || [];
      // hide product entirely if no in-stock sizes
      if (!sizes.length) return;

      var node = tpl.content.firstElementChild.cloneNode(true);

      var img = $(".b2g2-card__img", node);
      img.src = p.image || "";
      img.alt = p.title;
      $("[data-name]", node).textContent = p.title;

      var select = $("[data-b2g2-size]", node);
      var btn = $("[data-b2g2-add-gift]", node);

      // populate the size dropdown with in-stock variants only
      if (select) {
        select.innerHTML = "";
        sizes.forEach(function (s) {
          var opt = document.createElement("option");
          opt.value = s.id;
          opt.textContent = s.title;
          if (chosenIds[s.id]) opt.textContent += " ✓";
          select.appendChild(opt);
        });
      }

      // is any size of this product already chosen?
      var chosenSize = sizes.filter(function (s) { return chosenIds[s.id]; })[0];

      if (chosenSize) {
        btn.textContent = "Added ✓";
        btn.classList.add("is-added");
        btn.setAttribute("data-variant-id", chosenSize.id);
        btn.setAttribute("data-mode", "remove"); // clicking again removes it
        if (select) select.value = chosenSize.id;
      } else {
        btn.textContent = "Add Free Gift";
        btn.setAttribute("data-mode", "add");
        // default the button's target to the currently selected option
        if (select && select.value) btn.setAttribute("data-variant-id", select.value);
        if (state.remainingPicks <= 0) {
          btn.disabled = true;
          btn.classList.add("is-disabled");
        }
      }

      // keep button target in sync with dropdown choice
      if (select) {
        select.addEventListener("change", function () {
          btn.setAttribute("data-variant-id", select.value);
        });
      }

      track.appendChild(node);
    });
  }

  /* ---------- actions ---------- */
  function addGift(variantId) {
    return addToCart([{
      id: variantId,
      quantity: 1,
      properties: (function () {
        var p = {};
        p[giftProp] = "true";
        p["_b2g2_pack"] = "1";
        return p;
      })()
    }]).then(refresh);
  }

  function removeGiftLine(key) {
    var body = {}; body[key] = 0;
    // change.js by key
    return changeCart({ id: key, quantity: 0 }).then(refresh);
  }

  // Enforce: never more than FREE_N gifts; if locked, remove all gifts.
  function reconcileGifts(cart) {
    var state = analyze(cart);
    var chain = Promise.resolve(cart);

    if (!state.pickerUnlocked && state.giftLines.length) {
      // locked -> strip every gift
      state.giftLines.forEach(function (l) {
        chain = chain.then(function () { return changeCart({ id: l.key, quantity: 0 }); });
      });
      return chain.then(getCart);
    }

    if (state.currentGiftQty > FREE_N) {
      // too many -> trim newest first
      var over = state.currentGiftQty - FREE_N;
      var toTrim = state.giftLines.slice().reverse();
      for (var i = 0; i < toTrim.length && over > 0; i++) {
        (function (line) {
          var cut = Math.min(line.quantity, over);
          over -= cut;
          chain = chain.then(function () {
            return changeCart({ id: line.key, quantity: line.quantity - cut });
          });
        })(toTrim[i]);
      }
      return chain.then(getCart);
    }
    return Promise.resolve(cart);
  }

  /* ---------- main refresh cycle ---------- */
  var refreshing = false;
  function refresh() {
    if (refreshing) return Promise.resolve();
    refreshing = true;
    return getCart()
      .then(reconcileGifts)
      .then(function (cart) {
        var state = analyze(cart);
        renderTracker(state);
        renderMessaging(state);
        renderPicker(state);
        document.dispatchEvent(new CustomEvent("b2g2:updated", { detail: { cart: cart, state: state } }));
        return cart;
      })
      .catch(function (e) { console.warn("[b2g2] refresh error", e); })
      .finally(function () { refreshing = false; });
  }

  /* ---------- carousel scroll ---------- */
  function bindCarousel() {
    var vp = $("[data-b2g2-gift] .b2g2-gift__viewport");
    var prev = $("[data-b2g2-prev]");
    var next = $("[data-b2g2-next]");
    if (vp) {
      var step = 180;
      if (prev) prev.addEventListener("click", function () { vp.scrollBy({ left: -step, behavior: "smooth" }); });
      if (next) next.addEventListener("click", function () { vp.scrollBy({ left: step, behavior: "smooth" }); });
    }
    // recommended row carousel
    var rvp = $("[data-b2g2-reco] .b2g2-gift__viewport");
    var rprev = $("[data-b2g2-reco-prev]");
    var rnext = $("[data-b2g2-reco-next]");
    if (rvp) {
      var rstep = 180;
      if (rprev) rprev.addEventListener("click", function () { rvp.scrollBy({ left: -rstep, behavior: "smooth" }); });
      if (rnext) rnext.addEventListener("click", function () { rvp.scrollBy({ left: rstep, behavior: "smooth" }); });
    }
  }

  /* ---------- event delegation ---------- */
  document.addEventListener("click", function (e) {
    // Recommended paid single
    var recoBtn = e.target.closest ? e.target.closest("[data-b2g2-add-single]") : null;
    if (recoBtn && !recoBtn.disabled) {
      var rid = parseInt(recoBtn.getAttribute("data-variant-id"), 10);
      if (!rid) return;
      recoBtn.disabled = true;
      recoBtn.textContent = "Adding…";
      addPaidSingle(rid).finally(function () {});
      return;
    }

    var addBtn = e.target.closest ? e.target.closest("[data-b2g2-add-gift]") : null;
    if (addBtn && !addBtn.disabled) {
      var id = parseInt(addBtn.getAttribute("data-variant-id"), 10);
      if (!id) return;
      var mode = addBtn.getAttribute("data-mode");
      addBtn.disabled = true;
      if (mode === "remove") {
        // find the gift line with this variant and remove it
        getCart().then(function (cart) {
          var line = cart.items.filter(function (l) {
            return l.variant_id === id && isGiftLine(l);
          })[0];
          if (line) return changeCart({ id: line.key, quantity: 0 }).then(refresh);
          return refresh();
        });
      } else {
        addGift(id).finally(function () { /* re-render replaces node */ });
      }
    }
  });

  /* ---------- hook the theme's cart updates ----------
     Most themes dispatch a document event or re-render on fetch. We listen to a few
     common signals AND monkeypatch fetch to catch /cart/ mutations from the theme. */
  function hookThemeCart() {
    var _fetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = args[0] && args[0].url ? args[0].url : args[0];
      var p = _fetch.apply(this, args);
      if (typeof url === "string" && /\/cart\/(add|change|update|clear)/.test(url)) {
        p.then(function () { setTimeout(refresh, 60); }).catch(function () {});
      }
      return p;
    };
    // common theme events
    ["cart:refresh", "cart:updated", "cart:build", "ajaxCart:afterCartLoad"].forEach(function (ev) {
      document.addEventListener(ev, function () { setTimeout(refresh, 60); });
    });
  }

  /* ---------- init ---------- */
  function init() {
    hookThemeCart();
    bindCarousel();
    refresh();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // expose for debugging / manual calls
  window.B2G2 = { refresh: refresh, analyze: analyze };
})();